import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const CONTENT_ROW_EXTRA_SPACE = 8;

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useMeasuredPositionContentHeight(positionIds: readonly string[]) {
  const [heightPx, setHeightPx] = useState<number | null>(null);
  const elementsRef = useRef(new Map<string, HTMLElement>());
  const refCallbacksRef = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureFrameRef = useRef<number | null>(null);
  const idsKey = positionIds.join('|');

  const runMeasure = useCallback(() => {
    let maxHeight = 0;

    for (const element of elementsRef.current.values()) {
      const rectHeight = element.getBoundingClientRect().height;
      const measuredHeight = Math.max(rectHeight, element.offsetHeight);
      maxHeight = Math.max(maxHeight, measuredHeight);
    }

    const nextHeight = maxHeight > 0 ? Math.ceil(maxHeight + CONTENT_ROW_EXTRA_SPACE) : null;
    setHeightPx(current => (current === nextHeight ? current : nextHeight));
  }, []);

  // Coalesce measure() calls fired in the same frame (e.g. N ref callbacks on mount).
  const measure = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      runMeasure();
      return;
    }
    if (measureFrameRef.current !== null) return;
    measureFrameRef.current = window.requestAnimationFrame(() => {
      measureFrameRef.current = null;
      runMeasure();
    });
  }, [runMeasure]);

  useEffect(
    () => () => {
      if (measureFrameRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(measureFrameRef.current);
        measureFrameRef.current = null;
      }
    },
    [],
  );

  const getPositionContentRef = useCallback(
    (id: string) => {
      const cachedRef = refCallbacksRef.current.get(id);
      if (cachedRef) return cachedRef;

      const refCallback = (node: HTMLDivElement | null) => {
        const current = elementsRef.current.get(id);
        if (current && observerRef.current) {
          observerRef.current.unobserve(current);
        }

        if (node) {
          elementsRef.current.set(id, node);
          observerRef.current?.observe(node);
        } else {
          elementsRef.current.delete(id);
        }

        measure();
      };

      refCallbacksRef.current.set(id, refCallback);
      return refCallback;
    },
    [measure],
  );

  useEffect(() => {
    const liveIds = new Set(positionIds);
    for (const id of elementsRef.current.keys()) {
      if (!liveIds.has(id)) {
        elementsRef.current.delete(id);
      }
    }
    for (const id of refCallbacksRef.current.keys()) {
      if (!liveIds.has(id)) {
        refCallbacksRef.current.delete(id);
      }
    }
    measure();
  }, [idsKey, measure]);

  useIsomorphicLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      measure();
      return undefined;
    }

    const observer = new ResizeObserver(measure);
    observerRef.current = observer;
    for (const element of elementsRef.current.values()) {
      observer.observe(element);
    }
    measure();

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [idsKey, measure]);

  return {
    positionContentHeight: heightPx === null ? undefined : `${heightPx}px`,
    getPositionContentRef,
  };
}
